import { Badge, Card, CardBody, EmptyState, SkeletonRows } from '@ambernord/ui';
import { Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { productName } from '../lib/format.js';

interface ProductItem {
  id: string;
  category: string;
  sku: string;
  namesI18n: Record<string, string>;
  unit: string;
  active: boolean;
}

export default function Products() {
  const { t, i18n } = useTranslation('products');
  const [items, setItems] = useState<ProductItem[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: ProductItem[] }>('/api/products');
      if (!active) return;
      if (!res.ok) {
        setError(true);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <AppLayout title={t('title')}>
      {error ? (
        <DataState loading={false} error={t('error')} empty={false}>
          {null}
        </DataState>
      ) : items === null ? (
        <SkeletonRows rows={6} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Package className="h-6 w-6" />}
          title={t('empty.title')}
          hint={t('empty.hint')}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <Card key={p.id}>
              <CardBody className="flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      <Package className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {productName(p.namesI18n, i18n.language, p.sku)}
                      </p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{p.sku}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="amber">{p.category}</Badge>
                  <Badge tone="slate">
                    {t('columns.unit')}: {p.unit}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
